import React, { useState, useEffect } from "react";
import { useGlobal } from "utils/globalState";

import { trackEvent, BLACK_BOX_ELEMENT_ID } from "@jsainsburyplc/id-ui-common";

import getToken from "utils/getToken";
import { isIovationEnabled } from "utils/env";
import getRegistrationErrorMessage from "utils/getRegistrationErrorMessage";
import { registrationService as service } from "services/registration.service";
import selectRegFormData from "selectors/selectRegFormData";
import { buildFormData } from "utils/registrationFormHelpers";
import { actionTypes, categoryTypes } from "constants/tracking";

import { DENIED_IOVATION_RESPONSE } from "constants/codes";
import { registrationService } from "services/registration.service";

import RegistrationForm from "components/Registration/RegistrationForm";
import TokenExpiredNotification from "components/Registration/TokenExpiredNotification";
import PageFormat from "components/PageFormat";
import useDigitalDataTracking from "hooks/useDigitalTrack";

const trackFormSubmit = (action) => {
  trackEvent({
    category: categoryTypes.REGISTRATION,
    label: "Registration Form",
    action,
  });
};

const RegistrationPage = () => {
  const [registrationFormData] = useGlobal("screens");
  const [missionName] = useGlobal("readable_mission_name");

  const isIovationEnabledEnv = isIovationEnabled();

  useDigitalDataTracking(
    "web:groceries:authentication:register:enter details",
    "authentication",
    "authRegisterEnterDetails",
    "view"
  );
  const [regFormData] = useState(selectRegFormData(registrationFormData));
  const [token] = useState(getToken());
  const [email, setEmail] = useState(undefined);
  const [isTokenValid, setIsTokenValid] = useState(undefined);
  const [isLoading, setIsLoading] = useState(undefined);

  const onSubmit = async (values, { setSubmitting, setStatus }) => {
    setSubmitting(true);
    trackFormSubmit(actionTypes.SUBMIT);

    const blackBoxInput = document.getElementById(BLACK_BOX_ELEMENT_ID);
    const ioBlackBox = blackBoxInput ? blackBoxInput.value : "";

    const formData = buildFormData({ ioBlackBox, ...values }, regFormData);

    try {
      await registrationService.register(formData, getToken());
      setStatus({ success: true });
      trackFormSubmit(actionTypes.SUCCESS);
    } catch (error) {
      trackFormSubmit(actionTypes.ERROR);
      window.scrollTo(0, 0);
      if (error?.response?.body) {
        const data = await error.response.json();
        const { code } = data || {};
        if (code === DENIED_IOVATION_RESPONSE) {
          setStatus({ isBlocked: true });
        } else {
          setStatus({
            hasError: true,
            message: getRegistrationErrorMessage(code),
          });
        }
      } else {
        setStatus({
          hasError: true,
          message: getRegistrationErrorMessage(),
        });
      }
    }

    setSubmitting(false);
  };

  useEffect(() => {
    const validateToken = async () => {
      try {
        setIsLoading(true);
        const response = await service.checkToken(token);
        if (response.status === 200) {
          const data = await response.json();
          setIsTokenValid(true);
          setEmail(data.email);
        } else {
          throw new Error();
        }
      } catch (_) {
        setIsTokenValid(false);
      } finally {
        setIsLoading(false);
      }
    };
    token && validateToken();
  }, [token]);

  return (
    <PageFormat>
      {!isLoading && !isTokenValid && <TokenExpiredNotification />}
      {!isLoading && isTokenValid && (
        <RegistrationForm
          onSubmit={onSubmit}
          regFormData={regFormData}
          missionName={missionName}
          email={email}
          isIovationEnabled={isIovationEnabledEnv}
        />
      )}
    </PageFormat>
  );
};

export default RegistrationPage;
